import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { hasViewedCurrentLegalDocuments, markLegalDocumentViewed } from '../legal/acceptance';
import { LEGAL_POLICY_VERSION } from '../legal/policyVersion';
import { PRIVACY_POLICY, TERMS_OF_USE } from '../legal/legalDocuments';
import { LegalDocumentText } from './LegalDocumentText';

const SCROLL_END_PX = 32;

export function LegalConsentCheckbox({
    agreed,
    onChange,
    id = 'legal-consent',
    checkboxTestId = 'login-legal-consent',
    initialOpen = false,
}: {
    agreed: boolean;
    onChange: (next: boolean) => void;
    id?: string;
    checkboxTestId?: string;
    /** Open the review modal on first paint (tests). */
    initialOpen?: boolean;
}) {
    const [open, setOpen] = useState(initialOpen);
    const [reachedEnd, setReachedEnd] = useState(() => hasViewedCurrentLegalDocuments());
    const scrollRef = useRef<HTMLDivElement>(null);

    const markRead = useCallback(() => {
        markLegalDocumentViewed('privacy');
        markLegalDocumentViewed('terms');
        setReachedEnd(true);
    }, []);

    const checkScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollHeight <= el.clientHeight + SCROLL_END_PX) {
            markRead();
            return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_END_PX) {
            markRead();
        }
    }, [markRead]);

    useEffect(() => {
        if (!open) return;
        const frame = requestAnimationFrame(checkScroll);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, checkScroll]);

    const onConfirm = (checked: boolean) => {
        if (checked && !reachedEnd) return;
        onChange(checked);
        if (checked) setOpen(false);
    };

    return (
        <div className="rounded-lg border border-border bg-background/60" data-testid="legal-consent-block">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                <p className="text-xs text-foreground">
                    {agreed
                        ? `Accepted Privacy Policy and Terms of Use (version ${LEGAL_POLICY_VERSION}).`
                        : 'Review the Privacy Policy and Terms of Use before signing in.'}
                </p>
                <button
                    type="button"
                    className="shrink-0 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
                    onClick={() => setOpen(true)}
                    data-testid="legal-consent-open"
                >
                    {agreed ? 'Read again' : 'Review terms'}
                </button>
            </div>

            {open ? (
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4"
                    data-testid="legal-consent-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="legal-consent-title"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setOpen(false);
                    }}
                >
                    <div className="flex max-h-[min(90vh,40rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
                            <div>
                                <h2 id="legal-consent-title" className="text-sm font-semibold text-foreground">
                                    Privacy Policy and Terms of Use
                                </h2>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    Scroll to the bottom of both documents, then confirm. Version {LEGAL_POLICY_VERSION}.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                data-testid="legal-consent-close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div
                            ref={scrollRef}
                            onScroll={checkScroll}
                            className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-3"
                            data-testid="legal-consent-documents"
                        >
                            <LegalDocumentText body={PRIVACY_POLICY} compact />
                            <LegalDocumentText body={TERMS_OF_USE} compact />
                        </div>
                        <div className="space-y-3 border-t border-border px-4 py-3">
                            {!reachedEnd ? (
                                <p className="text-xs text-muted-foreground" data-testid="legal-consent-scroll-hint">
                                    Scroll all the way down to enable the checkbox.
                                </p>
                            ) : null}
                            <p className="text-xs text-muted-foreground">
                                We will store officer ID, name and email on the account, a password hash or SSO identity,
                                last login, activity logs (including IP), and optional digest emails you type in Settings.
                            </p>
                            <label htmlFor={id} className="flex items-start gap-2.5 text-xs text-foreground cursor-pointer">
                                <input
                                    id={id}
                                    type="checkbox"
                                    checked={agreed}
                                    disabled={!reachedEnd}
                                    onChange={(e) => onConfirm(e.target.checked)}
                                    className="mt-0.5 shrink-0"
                                    data-testid={checkboxTestId}
                                />
                                <span>
                                    I have read the Privacy Policy and Terms of Use. I agree that CryptoFlow may collect
                                    the data listed above for my official account. I understand activity is logged.
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
